import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { CampaignSummary, DonationSummary, Paginated } from '@foundation/shared';
import {
  DONATION_CHANNEL_LABEL_TH,
  DONATION_STATUSES,
  DONATION_STATUS_LABEL_TH,
} from '@foundation/shared';
import { api, buildQuery } from '../lib/api';
import { formatDateTime, formatSatang } from '../lib/format';
import { Empty, ErrorAlert, Loading } from '../components/ui';
import { useAuth } from '../auth';

const STATUS_TONE: Record<string, string> = {
  pending_verify: 'amber',
  verified: 'green',
  rejected: 'red',
  duplicate: '',
  refunded: 'red',
  void: '',
};

export default function DonationList() {
  const { allowed } = useAuth();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState<DonationSummary[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [q, setQ] = useState('');

  const status = params.get('status') ?? '';
  const campaignId = params.get('campaignId') ?? '';

  const load = useCallback(
    async (append = false, from?: string) => {
      setError(null);
      try {
        const res = await api<Paginated<DonationSummary>>(
          `/donations${buildQuery({ status, campaignId, q, limit: 50, cursor: from })}`,
        );
        setRows((prev) => (append && prev ? [...prev, ...res.data] : res.data));
        setCursor(res.nextCursor);
      } catch (e) {
        setError(e);
        if (!append) setRows([]);
      }
    },
    [status, campaignId, q],
  );

  useEffect(() => {
    const t = setTimeout(() => void load(), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  useEffect(() => {
    api<{ data: CampaignSummary[] }>('/campaigns')
      .then((r) => setCampaigns(r.data))
      .catch(() => setCampaigns([]));
  }, []);

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  const pendingTotal = (rows ?? [])
    .filter((r) => r.status === 'pending_verify')
    .reduce((sum, r) => sum + r.amount, 0);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>เงินบริจาค</h1>
          <p>ตรวจสอบสลิป จับคู่ผู้บริจาค และออกใบอนุโมทนาบัตร</p>
        </div>
        {allowed('donation:write') && (
          <Link className="btn" to="/donations/new">
            + บันทึกการบริจาค
          </Link>
        )}
      </div>

      <ErrorAlert error={error} />

      {status === 'pending_verify' && rows && rows.length > 0 && (
        <div className="alert info">
          รอตรวจสอบ {rows.length} รายการ · ยอดที่แจ้งมารวม {formatSatang(pendingTotal)}
        </div>
      )}

      <div className="card">
        <div className="toolbar">
          <div className="field grow">
            <label htmlFor="dq">ค้นหา</label>
            <input
              id="dq"
              type="search"
              placeholder="เลขที่ ชื่อผู้โอน ชื่อผู้บริจาค หรือเลขอ้างอิง"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="dstatus">สถานะ</label>
            <select id="dstatus" value={status} onChange={(e) => setParam('status', e.target.value)}>
              <option value="">ทั้งหมด</option>
              {DONATION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {DONATION_STATUS_LABEL_TH[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="dcamp">แคมเปญ</label>
            <select
              id="dcamp"
              value={campaignId}
              onChange={(e) => setParam('campaignId', e.target.value)}
            >
              <option value="">ทั้งหมด</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {rows === null ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>
            {status === 'pending_verify'
              ? 'ไม่มีสลิปรอตรวจสอบ 🎉'
              : 'ยังไม่มีรายการบริจาคตามเงื่อนไขนี้'}
          </Empty>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>เลขที่</th>
                    <th>ยอด</th>
                    <th>วันที่โอน</th>
                    <th className="wrap">ผู้บริจาค</th>
                    <th className="wrap">ชื่อบนสลิป</th>
                    <th>ช่องทาง</th>
                    <th>สลิป</th>
                    <th>สถานะ</th>
                    <th>ใบเสร็จ</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((d) => (
                    <tr key={d.id}>
                      <td className="mono small">
                        <Link className="row-link" to={`/donations/${d.id}`}>
                          {d.donationNo}
                        </Link>
                      </td>
                      <td style={{ fontWeight: 600 }}>{formatSatang(d.amount)}</td>
                      <td className="small muted">{formatDateTime(d.donatedAt)}</td>
                      <td className="wrap">
                        {d.donorName ?? <span className="badge amber">ยังไม่จับคู่</span>}
                      </td>
                      <td className="wrap small">{d.payerName ?? '—'}</td>
                      <td className="small">
                        {DONATION_CHANNEL_LABEL_TH[d.channel] ?? d.channel}
                      </td>
                      <td>{d.hasSlip ? '📎' : '—'}</td>
                      <td>
                        <span className={`badge ${STATUS_TONE[d.status] ?? ''}`}>
                          {DONATION_STATUS_LABEL_TH[d.status] ?? d.status}
                        </span>
                      </td>
                      <td className="mono small">{d.receiptNo ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {cursor && (
              <div style={{ marginTop: 14 }}>
                <button className="secondary" onClick={() => void load(true, cursor)}>
                  โหลดเพิ่ม
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
